import React, { useEffect, useRef } from "react";
import { useTranslation } from "@helpers/translate";
import bgCard1 from "@assets/img/bg-card1.png";
import bgCard2 from "@assets/img/bg-card2.png";
import bgCard3 from "@assets/img/bg-card3.png";
import bgCard4 from "@assets/img/bg-card4.png";
import binifyOrange from "@assets/img/binify-orange-big.png";
import "./FeaturesGrid.sass";

const MOBILE_BREAKPOINT = 1023;
const MOBILE_SCROLL_MULTIPLIER = 1.1;

/*
 * Значення повинні збігатися зі значеннями
 * в OfferFeaturesJourney.
 */
const MOBILE_ENTRY_START = 0.95;
const MOBILE_ENTRY_END = 0.42;

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

const interpolate = (start, end, progress) => {
  return start + (end - start) * progress;
};

const easeInOutCubic = (progress) => {
  if (progress < 0.5) {
    return 4 * progress * progress * progress;
  }

  return 1 - Math.pow(-2 * progress + 2, 3) / 2;
};

const FeaturesCard = ({
  title,
  description,
  bgImage,
  number,
  className = "",
}) => {
  return (
    <div
      className={`features-card ${className}`.trim()}
      style={{
        backgroundImage: `url(${bgImage})`,
      }}
    >
      <div className="features-card__content">
        <span className="features-card__number text-iron">
          {number}
        </span>

        <div className="features-card__title-wrapper">
          <h3 className="features-card__title">
            <span className="features-card__title-gradient">
              {title}
            </span>
          </h3>

          {description && (
            <p className="features-card__description text-storm-gray">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CenterCard = () => {
  return (
    <div className="center-card">
      <img
        src={binifyOrange}
        alt="Binify Shield"
        className="center-card__image"
      />
    </div>
  );
};

const FeaturesGrid = ({
  onMobileElementsChange,
}) => {
  const { __ } = useTranslation();

  const containerRef = useRef(null);
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);

  const mobileTrackRef = useRef(null);
  const mobileStickyRef = useRef(null);
  const mobileContentRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const mobileShieldTargetRef = useRef(null);

  const animationFrameRef = useRef(null);
  const mobileStickyStateRef = useRef("default");

  const mobileMeasurementsRef = useRef({
    maxTranslate: 0,
    stickyHeight: 0,
    cardScrollDistance: 0,
    horizontalStartOffset: 0,
  });

  useEffect(() => {
    onMobileElementsChange?.({
      track: mobileTrackRef.current,
      sticky: mobileStickyRef.current,
      content: mobileContentRef.current,
      scroll: mobileScrollRef.current,
      shieldTarget: mobileShieldTargetRef.current,
      measurementsRef: mobileMeasurementsRef,
    });

    return () => {
      onMobileElementsChange?.(null);
    };
  }, [onMobileElementsChange]);

  useEffect(() => {
    const isMobileLayout = () => {
      return window.innerWidth <= MOBILE_BREAKPOINT;
    };

    const setColumnsOpacity = (opacity) => {
      if (leftColRef.current) {
        leftColRef.current.style.opacity = `${opacity}`;
      }

      if (rightColRef.current) {
        rightColRef.current.style.opacity = `${opacity}`;
      }
    };

    const resetMobileStickyStyles = () => {
      const mobileSticky = mobileStickyRef.current;

      if (!mobileSticky) return;

      mobileSticky.style.position = "";
      mobileSticky.style.top = "";
      mobileSticky.style.left = "";
      mobileSticky.style.right = "";
      mobileSticky.style.bottom = "";
      mobileSticky.style.width = "";
      mobileSticky.style.height = "";
      mobileSticky.style.zIndex = "";

      mobileStickyStateRef.current = "default";
    };

    const resetMobileStyles = () => {
      if (mobileTrackRef.current) {
        mobileTrackRef.current.style.height = "";
      }

      resetMobileStickyStyles();

      if (mobileScrollRef.current) {
        mobileScrollRef.current.style.transform = "";
      }

      if (mobileContentRef.current) {
        mobileContentRef.current.style.transform = "";
      }

      mobileMeasurementsRef.current = {
        maxTranslate: 0,
        stickyHeight: 0,
        cardScrollDistance: 0,
        horizontalStartOffset: 0,
      };
    };

    const setMobileStickyState = ({
      nextState,
      trackRect,
      stickyHeight,
    }) => {
      const mobileSticky = mobileStickyRef.current;

      if (!mobileSticky) return;

      const currentState =
        mobileStickyStateRef.current;

      const fixedGeometryChanged =
        nextState === "fixed" &&
        (
          mobileSticky.style.left !==
            `${trackRect.left}px` ||
          mobileSticky.style.width !==
            `${trackRect.width}px` ||
          mobileSticky.style.height !==
            `${stickyHeight}px`
        );

      if (
        currentState === nextState &&
        !fixedGeometryChanged
      ) {
        return;
      }

      if (nextState === "fixed") {
        mobileSticky.style.position = "fixed";
        mobileSticky.style.top = "0";
        mobileSticky.style.left =
          `${trackRect.left}px`;
        mobileSticky.style.right = "auto";
        mobileSticky.style.bottom = "auto";
        mobileSticky.style.width =
          `${trackRect.width}px`;
        mobileSticky.style.height =
          `${stickyHeight}px`;
        mobileSticky.style.zIndex = "6";
      } else if (nextState === "bottom") {
        mobileSticky.style.position = "absolute";
        mobileSticky.style.top = "auto";
        mobileSticky.style.left = "0";
        mobileSticky.style.right = "auto";
        mobileSticky.style.bottom = "0";
        mobileSticky.style.width = "100%";
        mobileSticky.style.height =
          `${stickyHeight}px`;
        mobileSticky.style.zIndex = "6";
      } else {
        mobileSticky.style.position = "";
        mobileSticky.style.top = "";
        mobileSticky.style.left = "";
        mobileSticky.style.right = "";
        mobileSticky.style.bottom = "";
        mobileSticky.style.width = "";
        mobileSticky.style.height = "";
        mobileSticky.style.zIndex = "";
      }

      mobileStickyStateRef.current = nextState;
    };

    const updateMobileMeasurements = () => {
      const mobileTrack = mobileTrackRef.current;
      const mobileSticky = mobileStickyRef.current;
      const mobileScroll = mobileScrollRef.current;

      if (
        !mobileTrack ||
        !mobileSticky ||
        !mobileScroll
      ) {
        return;
      }

      if (!isMobileLayout()) {
        resetMobileStyles();
        return;
      }

      const stickyWidth =
        mobileSticky.clientWidth ||
        window.innerWidth;

      const stickyHeight =
        mobileSticky.clientHeight ||
        window.innerHeight;

      const maxTranslate = Math.max(
        mobileScroll.scrollWidth - stickyWidth,
        0,
      );

      const cardScrollDistance = Math.max(
        maxTranslate * MOBILE_SCROLL_MULTIPLIER,
        1,
      );

      const horizontalStartOffset =
        stickyHeight * MOBILE_ENTRY_END;

      mobileMeasurementsRef.current = {
        maxTranslate,
        stickyHeight,
        cardScrollDistance,
        horizontalStartOffset,
      };

      /*
       * Порожня відстань перед horizontal scroll
       * не додається до загальної висоти track.
       */
      const trackHeight = Math.max(
        stickyHeight +
          cardScrollDistance -
          horizontalStartOffset,
        stickyHeight + 1,
      );

      mobileTrack.style.height =
        `${trackHeight}px`;
    };

    const handleMobileScroll = () => {
      const mobileTrack = mobileTrackRef.current;
      const mobileSticky = mobileStickyRef.current;
      const mobileScroll = mobileScrollRef.current;
      const mobileContent = mobileContentRef.current;

      if (
        !mobileTrack ||
        !mobileSticky ||
        !mobileScroll ||
        !mobileContent
      ) {
        return;
      }

      const {
        maxTranslate,
        stickyHeight,
        cardScrollDistance,
        horizontalStartOffset,
      } = mobileMeasurementsRef.current;

      const viewportHeight = window.innerHeight;

      const trackRect =
        mobileTrack.getBoundingClientRect();

      const entryStart =
        viewportHeight * MOBILE_ENTRY_START;

      const entryEnd =
        viewportHeight * MOBILE_ENTRY_END;

      /*
       * Важлива зміна:
       *
       * fixed вмикається вже на початку entry-фази,
       * а не на початку руху першої картки.
       *
       * Завдяки цьому під час горизонтального руху
       * браузер більше не перемикає position.
       */
      let nextStickyState = "default";

      if (trackRect.bottom <= stickyHeight) {
        nextStickyState = "bottom";
      } else if (trackRect.top <= entryStart) {
        nextStickyState = "fixed";
      }

      setMobileStickyState({
        nextState: nextStickyState,
        trackRect,
        stickyHeight,
      });

      if (nextStickyState === "fixed") {
        /*
         * Sticky вже fixed на top: 0.
         *
         * Тому під час entry-фази внутрішній
         * контент спочатку компенсуємо вниз,
         * а потім плавно піднімаємо до top: 0.
         */
        const rawEntryProgress = clamp(
          (entryStart - trackRect.top) /
            (entryStart - entryEnd),
          0,
          1,
        );

        const entryProgress =
          easeInOutCubic(rawEntryProgress);

        const entryTranslateY = interpolate(
          entryStart,
          0,
          entryProgress,
        );

        mobileContent.style.transform =
          `translate3d(
            0,
            ${entryTranslateY}px,
            0
          )`;
      } else {
        /*
         * До entry та після завершення track
         * додаткова компенсація не потрібна.
         */
        mobileContent.style.transform =
          "translate3d(0, 0, 0)";
      }

      /*
       * Горизонтальна анімація починається
       * тільки після завершення entry-фази.
       *
       * На цей момент sticky уже давно fixed,
       * тому перша картка не має вертикального
       * тремтіння.
       */
      const horizontalScrolled = Math.max(
        horizontalStartOffset -
          trackRect.top,
        0,
      );

      const cardsProgress = clamp(
        horizontalScrolled /
          cardScrollDistance,
        0,
        1,
      );

      const translateX =
        maxTranslate * cardsProgress;

      mobileScroll.style.transform =
        `translate3d(-${translateX}px, 0, 0)`;
    };

    const handleDesktopScroll = () => {
      if (
        !containerRef.current ||
        !leftColRef.current ||
        !rightColRef.current
      ) {
        return;
      }

      const rect =
        containerRef.current
          .getBoundingClientRect();

      const viewportHeight =
        window.innerHeight;

      const startFade =
        viewportHeight * 0.8;

      const endFade =
        viewportHeight * 0.2;

      const opacity = clamp(
        (startFade - rect.top) /
          (startFade - endFade),
        0,
        1,
      );

      setColumnsOpacity(opacity);
    };

    const updateAnimation = () => {
      if (isMobileLayout()) {
        setColumnsOpacity(1);
        handleMobileScroll();
        return;
      }

      resetMobileStyles();
      handleDesktopScroll();
    };

    const requestAnimationUpdate = () => {
      if (
        animationFrameRef.current !== null
      ) {
        return;
      }

      animationFrameRef.current =
        window.requestAnimationFrame(() => {
          animationFrameRef.current = null;
          updateAnimation();
        });
    };

    const handleResize = () => {
      /*
       * Вимірювання виконуємо у звичайному layout,
       * а не в fixed-стані.
       */
      resetMobileStickyStyles();
      updateMobileMeasurements();
      requestAnimationUpdate();
    };

    window.addEventListener(
      "scroll",
      requestAnimationUpdate,
      {
        passive: true,
      },
    );

    window.addEventListener(
      "resize",
      handleResize,
    );

    let resizeObserver = null;

    if (
      typeof ResizeObserver !==
      "undefined"
    ) {
      resizeObserver =
        new ResizeObserver(() => {
          updateMobileMeasurements();
          requestAnimationUpdate();
        });

      if (mobileScrollRef.current) {
        resizeObserver.observe(
          mobileScrollRef.current,
        );
      }
    }

    updateMobileMeasurements();
    updateAnimation();

    return () => {
      window.removeEventListener(
        "scroll",
        requestAnimationUpdate,
      );

      window.removeEventListener(
        "resize",
        handleResize,
      );

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (
        animationFrameRef.current !== null
      ) {
        window.cancelAnimationFrame(
          animationFrameRef.current,
        );
      }

      resetMobileStyles();
    };
  }, []);

  return (
    <section
      className="features-flex padding-container"
      ref={containerRef}
    >
      <div className="features-flex__container">
        <div
          className="features-flex__column"
          ref={leftColRef}
          style={{ opacity: 0 }}
        >
          <FeaturesCard
            number="01"
            title={__(
              "main.features.fastExecution",
            )}
            description={__(
              "main.features.fastExecutionDesc",
            )}
            bgImage={bgCard1}
          />

          <FeaturesCard
            number="03"
            title={__(
              "main.features.flexibleCapital",
            )}
            bgImage={bgCard2}
            className="features-card--height-218"
          />
        </div>

        <div className="features-flex__center">
          <CenterCard />
        </div>

        <div
          className="features-flex__column"
          ref={rightColRef}
          style={{ opacity: 0 }}
        >
          <FeaturesCard
            number="02"
            title={__(
              "main.features.smartSupport",
            )}
            description={__(
              "main.features.smartSupportDesc",
            )}
            bgImage={bgCard3}
            className="features-card--align-right"
          />

          <FeaturesCard
            number="04"
            title={__(
              "main.features.skillBuilding",
            )}
            bgImage={bgCard4}
            className="features-card--height-218 features-card--align-right"
          />
        </div>

        <div
          className="features-flex__mobile-track"
          ref={mobileTrackRef}
        >
          <div
            className="features-flex__mobile-sticky"
            ref={mobileStickyRef}
          >
            <div
              className="features-flex__mobile"
              ref={mobileContentRef}
            >
              <div className="features-flex__mobile-center">
                <div
                  ref={mobileShieldTargetRef}
                  className="features-flex__mobile-shield-target"
                  aria-hidden="true"
                />
              </div>

              <div
                className="features-flex__mobile-scroll"
                ref={mobileScrollRef}
              >
                <div className="features-flex__mobile-card features-flex__mobile-card-first">
                  <FeaturesCard
                    number="01"
                    title={__(
                      "main.features.fastExecution",
                    )}
                    description={__(
                      "main.features.fastExecutionDesc",
                    )}
                    bgImage={bgCard1}
                  />
                </div>

                <div className="features-flex__mobile-card">
                  <FeaturesCard
                    number="02"
                    title={__(
                      "main.features.smartSupport",
                    )}
                    description={__(
                      "main.features.smartSupportDesc",
                    )}
                    bgImage={bgCard3}
                  />
                </div>

                <div className="features-flex__mobile-card">
                  <FeaturesCard
                    number="03"
                    title={__(
                      "main.features.flexibleCapital",
                    )}
                    bgImage={bgCard2}
                  />
                </div>

                <div className="features-flex__mobile-card features-flex__mobile-card-last">
                  <FeaturesCard
                    number="04"
                    title={__(
                      "main.features.skillBuilding",
                    )}
                    bgImage={bgCard4}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;
